import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { Figure, QuantileCells, ibamaCasesHref } from '@/pages/ibama/OverviewPage';
import { fetchIbamaConsistency, ibamaConsistencyParams } from '@/services/ibama';

const PAGE_SIZE = 50;

/**
 * IBAMA comparable groups, and the pair review that cannot be built.
 *
 * A pair needs two comparable cases whose outcomes diverged; IBAMA records
 * no amount or outcome code after either instance, so the queue ANEEL and
 * ANS show is refused with its funnel. What the ETL does build is the
 * grouping of every ledger case on (nature, legal classification, auto
 * year); the groups at the floor are listed alphabetically, each opening
 * the ledger, and their settled share is the state of debts, never a
 * ruling.
 */
export function IbamaConsistencyPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const [seed] = useSearchParams();
  const [nature, setNature] = useState(seed.get('nature') ?? '');
  const [year, setYear] = useState(seed.get('year') ?? '');
  const [query, setQuery] = useState(seed.get('query') ?? '');
  const filters = {
    nature: nature || null,
    year: year ? Number(year) : null,
    query: query || null,
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['ibama', 'consistency', filters],
    queryFn: ({ pageParam }) =>
      fetchIbamaConsistency({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const outcome = (code: string) => t(`ibama:overview.outcomes.${code}`, { defaultValue: code });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:consistency.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:consistency.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:consistency.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path={`/api/v1/ibama/export/consistency?${ibamaConsistencyParams(filters).toString()}`}
          label={t('ibama:consistency.export')}
        />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      {data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border bg-card p-4" data-shape>
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('ibama:consistency.shape.title')}
            </h2>
            <Answer answer={data.shape} help={t('ibama:consistency.shape.help')}>
              {(s) => (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Figure
                      label={t('ibama:consistency.shape.groups')}
                      value={`${number.format(s.groups_at_floor)} / ${number.format(s.groups)}`}
                    />
                    <Figure
                      label={t('ibama:consistency.shape.cases')}
                      value={`${number.format(s.cases_at_floor)} (${percent.format(
                        s.cases_at_floor_share,
                      )})`}
                    />
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-muted-foreground">
                      {t('ibama:consistency.shape.settledShare')}
                    </p>
                    <QuantileCells summary={s.settled_share} format={(v) => percent.format(v)} />
                  </div>
                </div>
              )}
            </Answer>
            <div className="mt-3">
              <FieldGuide i18nKey="ibama:consistency.guide" />
            </div>
          </section>

          <RefusalCard title={t('ibama:consistency.pairs.title')} answer={data.pair_review} />
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
        <div className="space-y-1">
          <label htmlFor="ibama-group-nature" className="block text-xs font-medium text-foreground">
            {t('ibama:consistency.queue.filterNature')}
          </label>
          <select
            id="ibama-group-nature"
            value={nature}
            onChange={(event) => setNature(event.target.value)}
            className="max-w-[24rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('ibama:consistency.queue.anyNature')}</option>
            {(data?.natures ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.value} ({number.format(option.groups)})
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="ibama-group-year" className="block text-xs font-medium text-foreground">
            {t('ibama:consistency.queue.filterYear')}
          </label>
          <input
            id="ibama-group-year"
            type="number"
            inputMode="numeric"
            value={year}
            onChange={(event) => setYear(event.target.value)}
            className="w-24 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="ibama-group-query" className="block text-xs font-medium text-foreground">
            {t('ibama:consistency.queue.filterQuery')}
          </label>
          <input
            id="ibama-group-query"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('ibama:consistency.queue.queryPlaceholder')}
            className="w-64 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          />
        </div>
      </div>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('ibama:consistency.queue.empty')} />
      ) : (
        <section className="rounded-lg border bg-card" data-stale={isPlaceholderData || undefined}>
          <div className="px-4 py-2 text-xs text-muted-foreground" data-total={data?.total}>
            {t('ibama:consistency.queue.count', {
              loaded: number.format(items.length),
              total: number.format(data?.total ?? 0),
            })}
          </div>
          <div ref={scrollRef} className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('ibama:consistency.queue.nature')}</th>
                  <th className="px-3 py-2 font-medium">
                    {t('ibama:consistency.queue.classification')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ibama:consistency.queue.year')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ibama:consistency.queue.members')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ibama:consistency.queue.settled')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ibama:consistency.queue.median')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('ibama:consistency.queue.outcomes')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((g) => {
                  const key = `${g.nature}|${g.legal_classification}|${g.notice_year ?? ''}`;
                  return (
                    <tr key={key} className="border-t align-top" data-group={key}>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{g.nature}</td>
                      <td className="px-3 py-2 text-foreground">{g.legal_classification}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {g.notice_year ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <Link
                          to={ibamaCasesHref({
                            nature: g.nature,
                            year: g.notice_year ?? undefined,
                          })}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {number.format(g.members)}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {t('ibama:consistency.queue.subjects', {
                            companies: number.format(g.companies),
                            persons: number.format(g.persons),
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {percent.format(g.settled_share)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {g.amount_median === null ? '—' : currency.format(g.amount_median)}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {Object.entries(g.outcomes)
                          .sort((a, b) => b[1] - a[1])
                          .map(([code, n]) => `${outcome(code)} ${number.format(n)}`)
                          .join(' · ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div ref={sentinelRef} />
          </div>
          {hasNextPage ? (
            <div className="border-t px-4 py-2 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className="text-sm text-primary underline-offset-2 hover:underline"
              >
                {isFetchingNextPage
                  ? t('common:state.loading')
                  : t('ibama:consistency.queue.loadMore')}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}
